(() => {
  if (customElements.get('drop-sales-week')) return;
  const bostonDate = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
  const calendarDay = date => {
    const parts = Object.fromEntries(bostonDate.formatToParts(date).map(part => [part.type, part.value]));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)) / 86400000;
  };
  const digitSegments = ['abcdef', 'bc', 'abdeg', 'abcdg', 'bcfg', 'acdfg', 'acdefg', 'abc', 'abcdefg', 'abcdfg'];
  const segmentShapes = {
    a: '8,0 40,0 44,4 40,8 8,8 4,4',
    b: '40,10 44,6 48,10 48,34 44,38 40,34',
    c: '40,50 44,46 48,50 48,78 44,82 40,78',
    d: '8,80 40,80 44,84 40,88 8,88 4,84',
    e: '0,50 4,46 8,50 8,78 4,82 0,78',
    f: '0,10 4,6 8,10 8,34 4,38 0,34',
    g: '8,40 40,40 44,44 40,48 8,48 4,44'
  };
  // Slowly expanding, shaded cloud clusters form the smoke plume.
  class FlareSmoke {
    constructor(canvas) {
      this.canvas = canvas;
      this.host = canvas.parentElement;
      this.time = 0;
      this.frame = null;
      this.running = false;
      this.events = new AbortController();
      this.gl = canvas.getContext('webgl', { alpha:true, premultipliedAlpha:true, antialias:false, depth:false, stencil:false, powerPreference:'low-power' });
      if (!this.gl) throw new Error('WebGL unavailable');
      try { this.initialize(); } catch (error) { this.dispose(); throw error; }
      canvas.addEventListener('webglcontextlost', event => {
        event.preventDefault();
        this.stop();
        this.host.classList.remove('dsw-smoke-ready');
        this.canvas.dataset.smokeState = 'fallback';
      }, { signal:this.events.signal });
      canvas.addEventListener('webglcontextrestored', () => {
        this.failed = false;
        try { this.initialize(); this.setRunning(this.shouldRun); }
        catch (_) { this.failed = true; this.stop(); this.host.classList.remove('dsw-smoke-ready'); }
      }, { signal:this.events.signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(canvas);
      }
    }
    initialize() {
      const gl = this.gl;
      const compile = (type, source) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { gl.deleteShader(shader); throw new Error('Smoke shader unavailable'); }
        return shader;
      };
      const vertex = compile(gl.VERTEX_SHADER, 'attribute vec2 position; varying vec2 uv; void main(){uv=position*.5+.5; gl_Position=vec4(position,0.,1.);}');
      let fragment;
      try {
        fragment = compile(gl.FRAGMENT_SHADER, `
          #ifdef GL_FRAGMENT_PRECISION_HIGH
          precision highp float;
          #else
          precision mediump float;
          #endif
          varying vec2 uv;
          uniform float time;
          float hash(vec2 p) {
            vec3 q=fract(vec3(p.xyx)*.1031);
            q+=dot(q,q.yzx+33.33);
            return fract((q.x+q.y)*q.z);
          }
          float noise(vec2 p) {
            vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f);
            return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x),f.y);
          }
          float fbm(vec2 p) {
            float value=0., weight=.5;
            mat2 turn=mat2(.8,-.6,.6,.8);
            for(int i=0;i<4;i++){value+=weight*noise(p);p=turn*p*2.03+vec2(12.3,7.1);weight*=.5;}
            return value;
          }
          void main() {
            vec4 smoke=vec4(0.);
            // Each cloud takes 25 seconds to rise. Its texture travels with it,
            // avoiding the fast vertical streaks that make noise resemble fire.
            for(int i=0;i<8;i++) {
              float age=fract(time*.04+float(i)/8.);
              float seed=float(i)*7.13;
              float py=.145+age*.78;
              float px=.39+.16*age+.026*age*sin(age*6.+seed);
              float radius=.018+.19*pow(age,.68);
              vec2 p=vec2((uv.x-px)*.667,uv.y-py)/radius;
              if(dot(p,p)>3.25) continue;
              float angle=seed+time*.045;
              p=mat2(cos(angle),-sin(angle),sin(angle),cos(angle))*p;
              float grain=fbm(p*2.4+vec2(seed,seed*.71));
              vec2 warp=vec2(noise(p*1.8+seed),noise(p*1.8+seed+8.))-.5;
              vec2 q=p+warp*.32;
              // A cluster of rounded lobes, with a continuous shaded interior.
              float d=length(q)/.82;
              d=min(d,length(q-vec2(-.48,.22))/.63);
              d=min(d,length(q-vec2(.43,.38))/.66);
              d=min(d,length(q-vec2(.38,-.39))/.6);
              d=min(d,length(q-vec2(-.31,-.43))/.64);
              d+=(grain-.48)*.4;
              float silhouette=1.-smoothstep(.76,1.13,d);
              float lifetime=smoothstep(0.,.085,age)*(1.-smoothstep(.68,1.,age));
              float alpha=silhouette*lifetime*.9;
              float volume=sqrt(max(0.,1.-min(d*d,1.)));
              float folds=smoothstep(.24,.7,grain);
              float light=clamp(.1+volume*.27+folds*.65+q.y*.1-q.x*.08,0.,1.);
              vec3 color=mix(vec3(.28,.016,.035),vec3(1.,.24,.25),pow(light,.8));
              // Only the lowest cloud catches the small flare's warm light.
              color=min(vec3(1.),color+vec3(.16,.045,.012)*(1.-smoothstep(.08,.24,age)));
              smoke.rgb=color*alpha+smoke.rgb*(1.-alpha);
              smoke.a=alpha+smoke.a*(1.-alpha);
            }
            float border=smoothstep(.01,.09,uv.x)*(1.-smoothstep(.91,1.,uv.x));
            border*=1.-smoothstep(.91,1.,uv.y);
            gl_FragColor=smoke*border;
          }
        `);
      } catch (error) { gl.deleteShader(vertex); throw error; }
      this.program=gl.createProgram();
      gl.attachShader(this.program,vertex); gl.attachShader(this.program,fragment); gl.linkProgram(this.program);
      gl.deleteShader(vertex); gl.deleteShader(fragment);
      if (!gl.getProgramParameter(this.program,gl.LINK_STATUS)) throw new Error('Smoke program unavailable');
      gl.useProgram(this.program);
      this.buffer=gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER,this.buffer);
      gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
      const position=gl.getAttribLocation(this.program,'position');
      gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
      this.timeUniform=gl.getUniformLocation(this.program,'time');
      this.resize();
      this.host.classList.add('dsw-smoke-ready');
      this.canvas.dataset.smokeState='paused';
    }
    resize() {
      if (this.failed || this.gl.isContextLost()) return;
      const rect=this.canvas.getBoundingClientRect();
      const scale=Math.min(window.devicePixelRatio || 1,1.25);
      this.canvas.width=Math.max(2,Math.min(420,Math.round(rect.width*scale)));
      this.canvas.height=Math.max(2,Math.round(this.canvas.width*(rect.height/Math.max(rect.width,1))));
      this.gl.viewport(0,0,this.canvas.width,this.canvas.height);
      this.draw();
    }
    draw() {
      this.gl.uniform1f(this.timeUniform,this.time+19.4);
      this.gl.drawArrays(this.gl.TRIANGLES,0,6);
    }
    setRunning(allowed) {
      this.shouldRun=allowed;
      if (!allowed || this.failed || this.gl.isContextLost()) { this.stop(); return; }
      if (this.running) return;
      this.running=true;
      this.lastDraw=null;
      this.canvas.dataset.smokeState='running';
      const tick=timestamp => {
        if (!this.running) return;
        if (this.lastDraw===null) this.lastDraw=timestamp;
        const elapsed=timestamp-this.lastDraw;
        // Cap drawing at 30fps; pause/resume never advances the simulation by wall-clock downtime.
        if (elapsed>=1000/30) {
          this.time+=Math.min(elapsed/1000,.1);
          this.lastDraw=timestamp;
          this.draw();
        }
        this.frame=requestAnimationFrame(tick);
      };
      this.frame=requestAnimationFrame(tick);
    }
    stop() {
      this.running=false;
      if (this.frame!==null) cancelAnimationFrame(this.frame);
      this.frame=null;
      this.canvas.dataset.smokeState=this.failed || this.gl.isContextLost() ? 'fallback' : 'paused';
    }
    dispose() {
      this.stop(); this.events.abort(); this.resizeObserver?.disconnect();
      if (this.program) this.gl.deleteProgram(this.program);
      if (this.buffer) this.gl.deleteBuffer(this.buffer);
      this.host.classList.remove('dsw-smoke-ready');
    }
  }
  class DropSalesWeek extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.setupFilters();
      this.setupCollection();
      this.setupDayCounter();
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.button = this.querySelector('[data-motion-toggle]');
      this.storageKey = 'the-impound-drop-sales-motion-paused';
      this.userPaused = false;
      try { this.userPaused = sessionStorage.getItem(this.storageKey) === 'true'; } catch (_) {}
      this.paused = this.userPaused || this.dataset.motion === 'off' || this.motionQuery.matches;
      const smokeCanvas = this.querySelector('[data-smoke-canvas]');
      if (smokeCanvas) {
        try { this.smoke = new FlareSmoke(smokeCanvas); }
        catch (_) { smokeCanvas.dataset.smokeState = 'fallback'; }
      }
      this.renderMotion();
      this.button?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        try { sessionStorage.setItem(this.storageKey, String(this.userPaused)); } catch (_) {}
        this.paused = this.userPaused || this.dataset.motion === 'off' || this.motionQuery.matches;
        this.renderMotion();
      }, { signal: this.controller.signal });
      this.motionChanged = () => {
        this.paused = this.userPaused || this.motionQuery.matches || this.dataset.motion === 'off';
        this.renderMotion();
      };
      this.motionQuery.addEventListener('change', this.motionChanged);
      this.visibilityChanged = () => {
        this.dataset.offscreen = String(document.hidden || !this.inView);
        this.syncSmokeMotion();
      };
      document.addEventListener('visibilitychange', this.visibilityChanged, { signal: this.controller.signal });
      this.inView = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.visibilityChanged();
        }, { rootMargin: '100px' });
        this.observer.observe(this.querySelector('.dsw-hero'));
      }
      this.visibilityChanged();
    }
    static saleDay(startDate, now = new Date()) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
      const [year, month, day] = startDate.split('-').map(Number);
      const start = new Date(Date.UTC(year, month - 1, day));
      if (year < 1000 || start.getUTCFullYear() !== year || start.getUTCMonth() !== month - 1 || start.getUTCDate() !== day) return null;
      // Compare calendar dates, not elapsed 24-hour periods: DST days can be 23/25 hours.
      return Math.max(0, calendarDay(now) - start.getTime() / 86400000 + 1);
    }
    static nextBostonMidnight(now = new Date()) {
      const today = calendarDay(now);
      let before = now.getTime();
      let after = before + 27 * 3600000;
      // Find the next Boston date change without assuming a fixed UTC offset.
      while (after - before > 1) {
        const middle = Math.floor((before + after) / 2);
        if (calendarDay(new Date(middle)) === today) before = middle;
        else after = middle;
      }
      return after;
    }
    setupDayCounter() {
      this.dayClock = this.querySelector('[data-day-clock]');
      if (!this.dayClock) return;
      this.dayReadout = this.dayClock.querySelector('[data-day-readout]');
      this.dayDigits = this.dayClock.querySelector('[data-day-digits]');
      this.dayFallback = this.dayClock.querySelector('[data-day-fallback]');
      this.dayStatus = this.dayClock.querySelector('[data-day-status]');
      this.lastSaleDay = null;
      const refresh = () => this.refreshDayCounter();
      document.addEventListener('visibilitychange', refresh, { signal: this.controller.signal });
      window.addEventListener('pageshow', refresh, { signal: this.controller.signal });
      window.addEventListener('focus', refresh, { signal: this.controller.signal });
      refresh();
    }
    refreshDayCounter() {
      if (this.dayTimer) clearTimeout(this.dayTimer);
      const now = new Date();
      const day = DropSalesWeek.saleDay(this.dayClock.dataset.dayStart, now);
      if (day === null) {
        this.dayFallback.textContent = 'Set a start date: YYYY-MM-DD';
        return;
      }
      if (day !== this.lastSaleDay) {
        this.lastSaleDay = day;
        this.dayReadout.hidden = day === 0;
        this.dayFallback.hidden = day > 0;
        if (day > 0) {
          this.dayDigits.innerHTML = String(day).split('').map(digit => `<svg viewBox="0 0 48 88" class="dsw-day-digit" aria-hidden="true" focusable="false">${Object.entries(segmentShapes).map(([segment, points]) => `<polygon points="${points}" class="${digitSegments[Number(digit)].includes(segment) ? 'is-lit' : ''}"/>`).join('')}</svg>`).join('');
          this.dayStatus.textContent = `Drop Sale Day ${day}, Boston time`;
        } else {
          this.dayFallback.textContent = `Starts ${this.dayClock.dataset.dayStart} · Boston time`;
          this.dayStatus.textContent = '';
        }
      }
      if (!document.hidden) this.dayTimer = setTimeout(() => this.refreshDayCounter(), DropSalesWeek.nextBostonMidnight(now) - now.getTime() + 50);
    }
    setupFilters() {
      this.filters = this.querySelector('[data-drop-filters]');
      this.cards = [...this.querySelectorAll('[data-drop-card]')];
      if (!this.filters || !this.cards.length) return;
      this.filterButtons = [...this.filters.querySelectorAll('[data-filter]')];
      this.filterStatus = this.querySelector('[data-filter-status]');
      this.filterEmpty = this.querySelector('[data-filter-empty]');
      this.activeFilter = 'all';
      this.filters.addEventListener('click', event => {
        const button = event.target.closest('[data-filter]');
        if (button && this.filters.contains(button)) {
          this.activeFilter = button.dataset.filter;
          this.renderFilters();
        }
      }, { signal: this.controller.signal });
      this.renderFilters();
      this.filters.hidden = false;
    }
    renderFilters() {
      if (!this.filterButtons) return;
      const matches = (card, key) => key === 'oc' ? card.dataset.originalConcept === 'true' : key === 'savings' ? Number(card.dataset.discount) >= 25 : true;
      let visible = 0;
      this.cards.forEach(card => { card.hidden = !matches(card, this.activeFilter); if (!card.hidden) visible++; });
      this.filterButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === this.activeFilter)));
      this.filters.querySelectorAll('[data-filter-count]').forEach(count => {
        count.textContent = this.cards.filter(card => matches(card, count.dataset.filterCount)).length;
      });
      this.filterStatus.textContent = `${visible} of ${this.cards.length} listings · Newest first`;
      this.filterEmpty.hidden = visible > 0 || this.loadingProducts;
    }
    setupCollection() {
      this.grid = this.querySelector('.dsw-products');
      this.pagination = this.querySelector('[data-collection-pagination]');
      if (!this.grid || !this.pagination) return;
      this.loadStatus = this.querySelector('[data-load-status]');
      this.retryButton = this.querySelector('[data-load-retry]');
      this.pageLinks = this.querySelector('[data-pagination-fallback]');
      const pageCount = Number(this.pagination.dataset.pages) || 1;
      const currentPage = Number(this.pagination.dataset.currentPage) || 1;
      this.remainingPages = Array.from({ length: pageCount }, (_, i) => i + 1).filter(page => page !== currentPage);
      this.sortCards();
      this.retryButton?.addEventListener('click', () => this.loadAllProducts(), { signal: this.controller.signal });
      if (this.remainingPages.length) this.loadAllProducts();
    }
    sortCards() {
      this.cards.sort((a, b) => Number(b.dataset.createdAt) - Number(a.dataset.createdAt) || b.dataset.productId.localeCompare(a.dataset.productId));
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.grid.append(...this.cards);
      if (focused?.isConnected) focused.focus({ preventScroll: true });
      this.renderFilters();
    }
    async loadAllProducts() {
      if (this.loadingProducts || !this.remainingPages.length) return;
      this.loadingProducts = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.retryButton.hidden = true;
      if (this.pageLinks) this.pageLinks.hidden = true;
      this.loadStatus.hidden = false;
      const signal = this.controller.signal;
      const seen = new Set(this.cards.map(card => card.dataset.productId));
      try {
        while (this.remainingPages.length) {
          this.loadStatus.textContent = `Loading all Drop Sales… ${this.cards.length} of ${this.pagination.dataset.total} loaded.`;
          const page = this.remainingPages[0];
          // Stay in this page's locale and theme preview, preserving section settings.
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', this.pagination.dataset.sectionId);
          url.searchParams.set(this.pagination.dataset.pageParam || 'page', String(page));
          const response = await fetch(url, { credentials: 'same-origin', signal });
          if (!response.ok) throw new Error('Collection page unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (signal.aborted) return;
          const incoming = parsed.getElementById(this.id);
          const pageInfo = incoming?.querySelector('[data-collection-pagination]');
          if (!pageInfo || Number(pageInfo.dataset.currentPage) !== page) throw new Error('Unexpected collection page');
          incoming.querySelectorAll('[data-drop-card]').forEach(card => {
            if (!seen.has(card.dataset.productId)) {
              seen.add(card.dataset.productId);
              this.cards.push(document.importNode(card, true));
            }
          });
          this.remainingPages.shift();
          this.sortCards();
        }
        this.loadStatus.textContent = `All ${this.cards.length} listings loaded. Newest first.`;
      } catch (error) {
        if (signal.aborted) return;
        this.loadStatus.textContent = `${this.cards.length} listings loaded. The remaining items couldn’t load. Retry or use the page links below.`;
        this.retryButton.hidden = false;
        if (this.pageLinks) this.pageLinks.hidden = false;
      } finally {
        if (!signal.aborted) {
          this.loadingProducts = false;
          this.grid.setAttribute('aria-busy', 'false');
          this.renderFilters();
        }
      }
    }
    renderMotion() {
      this.dataset.paused = String(this.paused);
      this.syncSmokeMotion();
      if (!this.button) return;
      this.button.hidden = this.motionQuery.matches || this.dataset.motion === 'off';
      this.button.querySelector('[data-motion-label]').textContent = this.paused ? 'Resume animation' : 'Pause animation';
    }
    syncSmokeMotion() {
      this.smoke?.setRunning(!this.paused && !document.hidden && this.inView !== false);
    }
    disconnectedCallback() {
      this.smoke?.dispose();
      this.smoke = null;
      if (this.dayTimer) clearTimeout(this.dayTimer);
      this.controller?.abort();
      this.observer?.disconnect();
      this.motionQuery?.removeEventListener('change', this.motionChanged);
      this.initialized = false;
      this.loadingProducts = false;
      this.filterButtons = null;
    }
  }
  customElements.define('drop-sales-week', DropSalesWeek);
})();
